<script setup lang="ts">
import { AppSwiperGallery } from '@libs/ui';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '../../../../../core/stores';
import type { ActivityProductCardData } from '../../../models';
import ImgProductCardLayout from '../ImgProductCardLayout.vue';
import ProductCardFooter from '../ProductCardFooter.vue';
import DateCardTag from '../DateCardTag.vue';

import ActivityProductCardContent from './ActivityProductCardContent.vue';

interface ActivityProductCardProps {
  date: string;
  productCardData: ActivityProductCardData;
  printMode: boolean;
}

const props = defineProps<ActivityProductCardProps>();

interface ActivityProductCardEmits {
  (event: 'show-details'): void;
  (event: 'change-product'): void;
}

const emit = defineEmits<ActivityProductCardEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();

function onGalleryImageSwipe(): void {
  statsigEventsStore.logTripOverviewActivityGalleryImageSwipe();
}
</script>

<template>
  <div class="activity-product-card">
    <ImgProductCardLayout :enable-hover-color="!props.printMode">
      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="!props.printMode"
          :show-number-of-slides="!props.printMode"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="coreStore.handleImageLoadError"
          :print-mode="props.printMode"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          @click="emit('show-details')"
          @gallery-image-swipe="onGalleryImageSwipe"
        />
      </template>
      <template #content>
        <ActivityProductCardContent
          :product-card-content="props.productCardData.productCardContent"
          @show-details="emit('show-details')"
        />
      </template>
      <template #footer>
        <ProductCardFooter @show-details="emit('show-details')" />
      </template>
      <template #tag>
        <DateCardTag :date="props.date" />
      </template>
    </ImgProductCardLayout>
  </div>
</template>
